import { useState, type SyntheticEvent } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { ArrowRight, KeyRound, Mail } from 'lucide-react';
import { useAuth } from '@/shared/auth/contexte';
import { lireEtat } from '@/shared/donnees/demo/magasin';
import { enModeDemo } from '@/shared/supabase/client';
import { Button, Champ } from '@/shared/ui';
import { Alerte, CadreConnexion } from './CadreConnexion';
import { cheminDeRetour } from './retour';

type Methode = 'mot-de-passe' | 'lien';

const EMAIL_VALIDE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function ConnexionPage() {
  const { etat, service } = useAuth();
  const [parametres] = useSearchParams();
  const [methode, setMethode] = useState<Methode>('mot-de-passe');
  const [email, setEmail] = useState('');
  const [motDePasse, setMotDePasse] = useState('');
  const [erreurs, setErreurs] = useState<{
    email?: string | undefined;
    motDePasse?: string | undefined;
  }>({});
  const [refus, setRefus] = useState<string | null>(null);
  const [lienEnvoye, setLienEnvoye] = useState(false);
  const [envoi, setEnvoi] = useState(false);

  if (
    etat.statut === 'connecte' ||
    etat.statut === 'sans-profil' ||
    etat.statut === 'injoignable'
  ) {
    return <Navigate to={cheminDeRetour(parametres.get('retour')) ?? '/'} replace />;
  }

  async function soumettre(evenement: SyntheticEvent) {
    evenement.preventDefault();
    const suivantes: typeof erreurs = {};
    if (!EMAIL_VALIDE.test(email.trim())) suivantes.email = 'Saisissez une adresse e-mail valide.';
    if (methode === 'mot-de-passe' && motDePasse.length === 0) {
      suivantes.motDePasse = 'Saisissez votre mot de passe.';
    }
    setErreurs(suivantes);
    setRefus(null);
    if (Object.keys(suivantes).length > 0) return;

    setEnvoi(true);
    const resultat =
      methode === 'mot-de-passe'
        ? await service.connexionMotDePasse(email.trim(), motDePasse)
        : await service.envoyerLienDeConnexion(email.trim());
    setEnvoi(false);

    if (!resultat.ok) setRefus(resultat.message);
    else if (methode === 'lien') setLienEnvoye(true);
  }

  return (
    <CadreConnexion
      titre="Connexion"
      sousTitre="Accès réservé au bureau et aux partenaires de VLA Énergie."
    >
      {lienEnvoye ? (
        <div className="flex flex-col gap-4">
          <Alerte ton="succes">
            Si un compte existe pour {email.trim()}, un lien de connexion vient d’y être envoyé. Il
            reste valable une heure.
          </Alerte>
          <Button
            onClick={() => {
              setLienEnvoye(false);
            }}
          >
            Revenir à la connexion
          </Button>
        </div>
      ) : (
        <form noValidate onSubmit={(e) => void soumettre(e)} className="flex flex-col gap-4">
          <div className="grid grid-cols-2 rounded-[10px] border border-border bg-surface p-1">
            {(
              [
                ['mot-de-passe', 'Mot de passe', KeyRound],
                ['lien', 'Lien par e-mail', Mail],
              ] as const
            ).map(([valeur, libelle, Icone]) => (
              <button
                key={valeur}
                type="button"
                aria-pressed={methode === valeur}
                onClick={() => {
                  setMethode(valeur);
                  setRefus(null);
                }}
                className={
                  methode === valeur
                    ? 'flex items-center justify-center gap-2 rounded-lg bg-primary-subtle py-1.5 text-[13px] font-medium text-primary'
                    : 'flex items-center justify-center gap-2 rounded-lg py-1.5 text-[13px] font-medium text-muted hover:text-ink'
                }
              >
                <Icone className="size-4" aria-hidden />
                {libelle}
              </button>
            ))}
          </div>

          <Champ
            libelle="Adresse e-mail"
            type="email"
            autoComplete="email"
            placeholder="prenom.nom@entreprise.fr"
            value={email}
            erreur={erreurs.email}
            onChange={(e) => {
              setEmail(e.target.value);
              setErreurs((p) => ({ ...p, email: undefined }));
            }}
          />
          {methode === 'mot-de-passe' ? (
            <Champ
              libelle="Mot de passe"
              type="password"
              autoComplete="current-password"
              value={motDePasse}
              erreur={erreurs.motDePasse}
              onChange={(e) => {
                setMotDePasse(e.target.value);
                setErreurs((p) => ({ ...p, motDePasse: undefined }));
              }}
            />
          ) : (
            <p className="text-xs text-muted">
              Vous recevrez un lien qui ouvre votre session sans mot de passe.
            </p>
          )}

          {refus ? <Alerte ton="danger">{refus}</Alerte> : null}

          <Button type="submit" variante="principal" disabled={envoi} className="mt-1 h-11">
            {methode === 'mot-de-passe' ? 'Se connecter' : 'Recevoir le lien'}
            <ArrowRight aria-hidden />
          </Button>

          <Link
            to="/mot-de-passe-oublie"
            className="text-center text-[13px] font-medium text-primary"
          >
            Mot de passe oublié
          </Link>
        </form>
      )}

      {enModeDemo() ? <ComptesDemo /> : null}
    </CadreConnexion>
  );
}

function ComptesDemo() {
  const { service } = useAuth();
  return (
    <div className="mt-8 rounded-xl border border-dashed border-note-border bg-note-bg p-4">
      <p className="mb-3 text-xs font-medium text-note">
        Mode démo — aucune donnée réelle. Entrer comme :
      </p>
      <div className="flex flex-col gap-2">
        {lireEtat().comptes.map((compte) => (
          <Button
            key={compte.id}
            taille="sm"
            className="justify-start"
            onClick={() => void service.connexionMotDePasse(compte.email, 'demo')}
          >
            <span className="font-semibold">{compte.nom || compte.email}</span>
            <span className="text-muted">
              {compte.role === 'bureau'
                ? 'bureau'
                : (compte.partenaireNom ?? 'partenaire non rattaché')}
            </span>
          </Button>
        ))}
      </div>
    </div>
  );
}
